import { FormEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CalendarPlus, FileDiff, FileQuestion, RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import {
  ActivityItem,
  Angaben,
  Betragsfeld,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  EmptyState,
  Faelligkeit,
  Skeleton,
  StatusPill,
  formatDatum,
  formatDatumZeit,
  formatEuro,
  toast,
} from '@elbklar/ui';

import { SelectField, TextField, TextareaField } from '@/components/Felder';
import { RouterLink } from '@/components/layout/RouterLink';
import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { radarApi } from '@/lib/api';
import { betragFehler } from '@/lib/betrag';
import { datum, hatFehler, istUuid, ohne, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { RadarChange, RadarDeadlineKind, RadarProgram, RadarStatus } from '@/types';
import {
  DEADLINE_KIND_TEXT,
  EFFORT_TEXT,
  KIND_TEXT,
  REWARD_KIND_TEXT,
  SOURCE_STATE_HINWEIS,
  STATUS_REIHENFOLGE,
  aenderungText,
  ebeneText,
  optionen,
  schluesselText,
  statusText,
  wertText,
} from './radarText';
import { QuellLinks, Quellzustand, Score, useVorwarnungTage } from './Bausteine';
import { GeaendertDialog, PruefKnoepfe } from './PruefauftraegeBlock';
import { ausProgramm, geaendert, pruefen, zusammenfuehren, type Formular, type FormularFeld } from './formular';

/** Was die Liste mitgibt, damit „Zurück zum Radar“ dort landet, wo man war. */
interface Herkunft {
  listenSuche?: string;
}

/**
 * Die Akte eines Programms als eigene Seite (`/radar/:id`, U8).
 *
 * Bis D2 ein Dialog über der Liste; Links aus Radar-Mails und der Glocke
 * liefen ins Leere (`/radar?program=…` las niemand). Jetzt hat jeder Eintrag
 * eine Adresse, und die Anmeldung führt dorthin zurück.
 *
 * Oben steht, was der Roboter gelesen und bewertet hat; daneben, was der
 * Mensch daraus macht: Status, Notiz, korrigierte Fristen. Am Handy kommt
 * die Bearbeitung zuerst (Status, Notiz, Prüfauftrag, „Quelle öffnen“), die
 * Einzelheiten darunter. Gespeichert wird nur, was hier bearbeitbar ist.
 */
export function ProgrammSeite() {
  // Nur eine UUID geht an die API; alles andere ist „nicht gefunden“, ohne Anfrage.
  const { id: adressId } = useParams<{ id: string }>();
  // `key`: Von einem Eintrag zum nächsten (Glocke, Zurück/Vorwärts im Browser)
  // hängt React dieselbe Route nicht neu ein. Ohne neuen Schlüssel blieben ein
  // offenes „In Planung übernehmen“ mit Betrag und Datum von A, Feldfehler und
  // das Befund-Sheet auf der Seite von B stehen (Gegenprüfung D2).
  return <ProgrammInhalt key={adressId ?? ''} id={istUuid(adressId) ? adressId : undefined} />;
}

function ProgrammInhalt({ id }: { id: string | undefined }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formId = useId();

  const listenSuche = (location.state as Herkunft | null)?.listenSuche ?? '';
  const vorwarnungTage = useVorwarnungTage();
  const zurueck = { label: 'Zurück zum Radar', href: `/radar${listenSuche}`, linkAs: RouterLink };

  // Kein Nachladen beim Fokuswechsel: Anton öffnet die Quelle in einem neuen
  // Tab und kommt zurück. Ein neuer Stand vom Server würde ohnehin
  // abgeglichen (`zusammenfuehren`), aber nicht ohne Not.
  const programm = useQuery({
    queryKey: ['radar', 'program', id],
    queryFn: () => radarApi.program(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    retry: (versuch, fehler: { response?: { status?: number } }) => fehler.response?.status !== 404 && versuch < 1,
  });

  // Erst, wenn es den Eintrag gibt: Für eine falsche Adresse reicht eine 404.
  const historie = useQuery({
    queryKey: ['radar', 'changes', id],
    queryFn: () => radarApi.changes(id!),
    enabled: Boolean(programm.data),
  });

  // Der zuletzt geladene Stand und das Formular darüber. Ein neuer Stand vom
  // Server (nach „Geprüft — geändert“ etwa) wird abgeglichen, nicht über das
  // Getippte geschrieben.
  const basisRef = useRef<{ id: string; formular: Formular } | null>(null);
  const [basis, setBasis] = useState<Formular | null>(null);
  const [formular, setFormular] = useState<Formular | null>(null);
  const [fehler, setFehler] = useState<Fehler<FormularFeld>>({});
  const [loeschenBestaetigen, setLoeschenBestaetigen] = useState(false);
  const [geaendertFuer, setGeaendertFuer] = useState<RadarProgram | null>(null);

  const geladenId = programm.data?.id;
  const geladenStand = programm.data?.updated_at;
  useEffect(() => {
    const p = programm.data;
    if (!p) return;
    const server = ausProgramm(p);
    const vorher = basisRef.current;
    basisRef.current = { id: p.id, formular: server };
    setBasis(server);
    setFormular((lokal) => (vorher && vorher.id === p.id && lokal ? zusammenfuehren(vorher.formular, lokal, server) : server));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geladenId, geladenStand]);

  const nachAenderung = () => {
    void queryClient.invalidateQueries({ queryKey: ['radar'] });
  };

  const speichern = useMutation({
    mutationFn: (f: Formular) =>
      radarApi.updateProgram(programm.data!.id, {
        status: f.status,
        note: f.note.trim() || null,
        deadline: f.deadline || null,
        deadline_kind: f.deadline ? 'fest' : f.deadline_kind,
        event_date: f.event_date || null,
        event_end_date: f.event_end_date || null,
        review_at: f.review_at || null,
        manual_search_query: f.manual_search_query.trim() || null,
      }),
    onSuccess: (neu, gesendet) => {
      toast.success('Gespeichert');
      // Was seit dem Absenden noch getippt wurde, bleibt; der Rest ist der
      // neue Stand (die API trimmt etwa die Notiz).
      const server = ausProgramm(neu);
      basisRef.current = { id: neu.id, formular: server };
      setBasis(server);
      setFormular((lokal) => (lokal ? zusammenfuehren(gesendet, lokal, server) : server));
      queryClient.setQueryData(['radar', 'program', id], neu);
      nachAenderung();
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  const unveraendert = useMutation({
    mutationFn: () => radarApi.checked(programm.data!.id, { changed: false }),
    onSuccess: () => {
      toast.success('Geprüft — unverändert');
      nachAenderung();
    },
    onError: () => toast.error('Vermerken fehlgeschlagen'),
  });

  // Der Fehler erscheint im Bestätigungsdialog, der dafür offen bleibt.
  const loeschen = useMutation({
    mutationFn: () => radarApi.removeProgram(programm.data!.id),
    onSuccess: () => {
      toast.info('Eintrag gelöscht');
      queryClient.removeQueries({ queryKey: ['radar', 'program', id] });
      nachAenderung();
      navigate(`/radar${listenSuche}`, { replace: true });
    },
  });

  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formular) return;
    const neu = pruefen(formular, unlesbareFelder(event.currentTarget));
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate(formular);
  };

  const setze = <K extends keyof Formular>(feld: K, wert: Formular[K]) => {
    setFormular((f) => (f ? { ...f, [feld]: wert } : f));
    if (feld in fehler) setFehler((alt) => ohne(alt, feld as FormularFeld));
    // „Ende vor Beginn“ hängt an beiden Feldern.
    if (feld === 'event_date') setFehler((alt) => ohne(alt, 'event_end_date'));
  };

  if (!id || programm.isError) {
    const fehlt = !id || (programm.error as { response?: { status?: number } }).response?.status === 404;
    return (
      <div className="space-y-6">
        <SeitenKopf title="Radar" zurueck={zurueck} size="page" />
        <Card>
          {fehlt ? (
            <EmptyState
              icon={FileQuestion}
              title="Eintrag nicht gefunden"
              description="Er wurde gelöscht, oder die Adresse stimmt nicht."
              action={
                <Button asChild variant="primary" size="lg">
                  <Link to="/radar">Zum Radar</Link>
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={TriangleAlert}
              title="Eintrag konnte nicht geladen werden"
              description="Die Verbindung ist gestört. Gleich noch einmal versuchen."
              action={
                <Button size="lg" icon={RefreshCw} onClick={() => void programm.refetch()}>
                  Erneut laden
                </Button>
              }
            />
          )}
        </Card>
      </div>
    );
  }

  const p = programm.data;
  if (!p || !formular) {
    return (
      <div className="space-y-6">
        <SeitenKopf title="Eintrag wird geladen" zurueck={zurueck} size="page" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 w-full lg:col-span-2" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const istEvent = p.kind === 'event';
  const ungespeichert = geaendert(basis, formular);

  return (
    <div className="space-y-6">
      <SeitenKopf
        size="page"
        zurueck={zurueck}
        title={p.title}
        subtitle={[KIND_TEXT[p.kind], p.actor_name ?? p.provider, p.region, ebeneText(p.level)].filter(Boolean).join(' · ')}
        // Löschen steht am Fuß der Seite; am Handy zusätzlich im Menü „…“,
        // damit es ohne Scrollen erreichbar ist. Am Rechner kein roter Knopf
        // neben der Hauptaktion.
        mehrAktionen={[
          { label: 'Eintrag löschen', icon: Trash2, tone: 'danger', nurHandy: true, onSelect: () => setLoeschenBestaetigen(true) },
        ]}
        actions={
          <>
            {ungespeichert && <span className="t-small text-muted-foreground">Nicht gespeichert</span>}
            <Button type="submit" form={formId} variant="primary" loading={speichern.isPending}>
              Änderungen speichern
            </Button>
          </>
        }
      />

      <form noValidate id={formId} onSubmit={absenden} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Seitenspalte: am Handy zuerst, am Rechner rechts. */}
        <div className="min-w-0 space-y-6 lg:col-start-3 lg:row-start-1">
          <Card title="Bearbeitung">
            <div className="space-y-4">
              <SelectField
                label="Status"
                options={STATUS_REIHENFOLGE.map((wert) => ({ value: wert, label: statusText(wert) }))}
                value={formular.status}
                onChange={(e) => setze('status', e.target.value as RadarStatus)}
              />
              <TextareaField
                label="Notiz"
                rows={5}
                placeholder="Was bekannt ist, wer gefragt wurde, was noch fehlt …"
                value={formular.note}
                onChange={(e) => setze('note', e.target.value)}
              />
              <TextField
                label="Wiedervorlage"
                type="date"
                name="review_at"
                hint="Wann wieder draufgeschaut wird"
                value={formular.review_at}
                onChange={(e) => setze('review_at', e.target.value)}
                error={fehler.review_at}
              />
            </div>
          </Card>

          <Card title="Quelle">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Quellzustand zustand={p.source_state} />
                <QuellLinks programm={p} />
              </div>
              <p className="t-small text-muted-foreground">
                {SOURCE_STATE_HINWEIS[p.source_state]}
                {p.fetch_error && <span className="ml-1 text-danger">({p.fetch_error})</span>}
              </p>

              {p.needs_manual_check && (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <p className="t-small text-muted-foreground">
                    {p.manual_check_due ? 'Prüfung fällig. ' : ''}
                    Quelle öffnen, nachsehen, Ergebnis vermerken.
                  </p>
                  <PruefKnoepfe
                    laeuft={unveraendert.isPending}
                    onUnveraendert={() => unveraendert.mutate()}
                    onGeaendert={() => setGeaendertFuer(p)}
                  />
                </div>
              )}

              <TextField
                label="Suchbegriff für die händische Prüfung"
                placeholder={p.search_query ?? 'Titel, Träger, Jahr'}
                value={formular.manual_search_query}
                onChange={(e) => setze('manual_search_query', e.target.value)}
              />

              <Angaben
                items={[
                  { label: 'Zuletzt abgerufen', value: formatDatumZeit(p.last_fetched_at), mono: true },
                  { label: 'Zuletzt geändert', value: formatDatumZeit(p.last_changed_at), mono: true },
                  { label: 'Händisch geprüft', value: formatDatumZeit(p.last_manual_check_at), mono: true },
                  { label: 'Herkunft', value: [p.origin, p.first_seen_at && `seit ${formatDatum(p.first_seen_at)}`].filter(Boolean).join(' · ') },
                ]}
              />
            </div>
          </Card>
        </div>

        {/* Hauptspalte: was der Roboter gelesen und bewertet hat, Fristen, Planung, Verlauf. */}
        <div className="min-w-0 space-y-6 lg:col-span-2 lg:col-start-1 lg:row-start-1">
          {(p.summary || p.target_group || p.themes.length > 0) && (
            <Card title="Worum es geht">
              {p.themes.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1">
                  {p.themes.map((thema) => (
                    <StatusPill key={thema} punkt={false}>
                      {thema}
                    </StatusPill>
                  ))}
                </div>
              )}
              {p.summary && <p className="whitespace-pre-line">{p.summary}</p>}
              {p.target_group && (
                <p className="mt-2 text-muted-foreground">
                  <span className="font-medium text-foreground">Zielgruppe: </span>
                  {p.target_group}
                </p>
              )}
            </Card>
          )}

          <Card title="Konditionen">
            <Angaben items={konditionen(p)} />
          </Card>

          <Card title="Bewertung">
            <div className="flex flex-wrap items-center gap-4">
              <Score score={p.score} className="w-40" />
              {p.estimated_effort && (
                <span className="text-muted-foreground">
                  Aufwand: <strong className="font-medium text-foreground">{EFFORT_TEXT[p.estimated_effort]}</strong>
                </span>
              )}
            </div>
            {p.score_rationale && <p className="mt-2 whitespace-pre-line">{p.score_rationale}</p>}
            {p.exclusion_reason && (
              <Callout tone="warning" icon={TriangleAlert} title="Ausschlussgrund" boxed className="mt-3">
                {p.exclusion_reason}
              </Callout>
            )}
            {p.score === null && !p.score_rationale && !p.exclusion_reason && (
              <p className="text-muted-foreground">Noch nicht bewertet.</p>
            )}
          </Card>

          <Card title="Fristen">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label={istEvent ? 'Anmeldeschluss' : 'Frist'}
                type="date"
                name="deadline"
                hint={fristHinweis(formular.deadline, vorwarnungTage)}
                value={formular.deadline}
                onChange={(e) => setze('deadline', e.target.value)}
                error={fehler.deadline}
              />
              <SelectField
                label="Fristart"
                options={optionen(DEADLINE_KIND_TEXT)}
                disabled={Boolean(formular.deadline)}
                hint={formular.deadline ? 'mit Datum: feste Frist' : undefined}
                value={formular.deadline ? 'fest' : formular.deadline_kind}
                onChange={(e) => setze('deadline_kind', e.target.value as RadarDeadlineKind)}
              />
              <TextField
                label={istEvent ? 'Datum' : 'Termin'}
                type="date"
                name="event_date"
                value={formular.event_date}
                onChange={(e) => setze('event_date', e.target.value)}
                error={fehler.event_date}
              />
              <TextField
                label="Ende"
                type="date"
                name="event_end_date"
                min={formular.event_date || undefined}
                value={formular.event_end_date}
                onChange={(e) => setze('event_end_date', e.target.value)}
                error={fehler.event_end_date}
              />
            </div>
          </Card>

          <PlanungKarte programm={p} onUebernommen={() => void programm.refetch()} />

          <Card title="Änderungshistorie">
            {historie.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : !historie.data || historie.data.length === 0 ? (
              <p className="text-muted-foreground">Noch keine Änderung an der Quelle festgestellt.</p>
            ) : (
              <div>
                {historie.data.map((aenderung, i) => (
                  <Aenderung key={aenderung.id} aenderung={aenderung} last={i === historie.data.length - 1} />
                ))}
              </div>
            )}
          </Card>

          <div>
            <Button variant="ghost" icon={Trash2} className="text-danger" onClick={() => setLoeschenBestaetigen(true)}>
              Eintrag löschen
            </Button>
          </div>
        </div>
      </form>

      <GeaendertDialog programm={geaendertFuer} onSchliessen={() => setGeaendertFuer(null)} onGespeichert={nachAenderung} />

      <ConfirmDialog
        open={loeschenBestaetigen}
        onOpenChange={setLoeschenBestaetigen}
        title="Eintrag löschen"
        description={`„${p.title}“ wird aus dem Radar gelöscht, samt Notiz und Änderungshistorie.`}
        confirmLabel="Eintrag löschen"
        tone="danger"
        onConfirm={() => loeschen.mutateAsync()}
      />
    </div>
  );
}

/** Die Frist unter dem Fristfeld, im Wortlaut der Liste. */
function fristHinweis(frist: string, vorwarnungTage: number): ReactNode {
  if (!frist || datum(frist)) return undefined;
  return <Faelligkeit datum={frist} relativ vorwarnungTage={vorwarnungTage} vergangenRuhig />;
}

/**
 * Eine Änderung an der Quelle im Verlauf: Was der Roboter festgestellt hat,
 * wann, und welche Felder er daraus übernommen hat. Relevantes in `warning`
 * (jemand sollte es lesen), Unwesentliches und Unbewertetes ruhig.
 */
function Aenderung({ aenderung, last }: { aenderung: RadarChange; last: boolean }) {
  const felder = Object.entries(aenderung.field_changes);
  const bewertung = aenderung.relevant === null ? 'nicht bewertet' : aenderung.relevant ? 'relevant' : 'unwesentlich';
  return (
    <ActivityItem
      icon={FileDiff}
      tone={aenderung.relevant ? 'warning' : 'neutral'}
      title={aenderung.summary ?? 'Änderung an der Quelle'}
      meta={`Radar · ${formatDatumZeit(aenderung.detected_at)} · ${bewertung}`}
      last={last}
    >
      {felder.length > 0 && (
        <Angaben
          className="mt-2"
          items={felder.map(([feld, wert]) => ({ label: schluesselText(feld), value: aenderungText(wert) }))}
        />
      )}
    </ActivityItem>
  );
}

/** Die Konditionen als Angaben; was fehlt, steht als „–“ da. */
function konditionen(p: RadarProgram) {
  const euro = (wert: string) => formatEuro(wert, { cent: true });
  const betrag =
    p.amount_min && p.amount_max
      ? `${euro(p.amount_min)} – ${euro(p.amount_max)}`
      : p.amount_max
        ? `bis ${euro(p.amount_max)}`
        : p.amount_min
          ? `ab ${euro(p.amount_min)}`
          : null;
  const zeilen: { label: string; value: ReactNode }[] =
    p.kind === 'wettbewerb'
      ? [
          {
            label: 'Gewinn',
            value: [p.reward_kind ? REWARD_KIND_TEXT[p.reward_kind] : null, p.amount_max ? euro(p.amount_max) : null]
              .filter(Boolean)
              .join(' · '),
          },
        ]
      : [
          { label: 'Betrag', value: betrag },
          { label: 'Förderquote', value: p.funding_rate },
        ];
  if (p.subkind) zeilen.push({ label: 'Art', value: p.subkind });
  zeilen.push(
    { label: 'Antragsweg', value: p.application_path },
    { label: 'Voraussetzungen', value: p.prerequisites && <span className="whitespace-pre-line">{p.prerequisites}</span> },
    { label: 'Kombinierbar mit', value: p.combinable_with }
  );
  if (p.kind === 'event') zeilen.push({ label: 'Ort', value: p.location });
  for (const [schluessel, wert] of Object.entries(p.details)) {
    zeilen.push({ label: schluesselText(schluessel), value: wertText(wert) });
  }
  return zeilen;
}

/**
 * Findestelle → Haltestelle: Der erwartete Zufluss wandert als geplante
 * Einnahme (Entwurf) in die Planung. Der Betrag läuft über das Betragsfeld
 * und geht als Decimal-Text an die API, nie als Gleitkommazahl (Audit FIN-1
 * bis 3; bis D2 ein Zahlenfeld mit `Number()`).
 */
function PlanungKarte({ programm: p, onUebernommen }: { programm: RadarProgram; onUebernommen: () => void }) {
  const [planung, setPlanung] = useState<{ betrag: string | null; datum: string } | null>(null);
  const [fehler, setFehler] = useState<Fehler<'betrag' | 'datum'>>({});

  const planen = useMutation({
    mutationFn: (daten: { amount: string; due_date: string }) => radarApi.planProgram(p.id, daten),
    onSuccess: () => {
      toast.success('In die Planung übernommen — als Entwurf, Betrag und Datum lassen sich dort ändern.');
      setPlanung(null);
      onUebernommen();
    },
    onError: (fehler: { response?: { data?: { detail?: unknown } } }) =>
      toast.error(typeof fehler.response?.data?.detail === 'string' ? fehler.response.data.detail : 'Übernahme fehlgeschlagen'),
  });

  const uebernehmen = () => {
    if (!planung) return;
    const neu = {
      betrag: betragFehler(planung.betrag, { nichtNull: true, nurPositiv: true }),
      datum: datum(planung.datum),
    };
    setFehler(neu);
    if (hatFehler(neu) || !planung.betrag) return;
    planen.mutate({ amount: planung.betrag, due_date: planung.datum });
  };

  return (
    <Card title="Planung">
      {p.planned_item_id ? (
        <p className="text-muted-foreground">
          Steht in der{' '}
          <Link to="/planung" className="font-medium text-link hover:underline">
            Planung
          </Link>{' '}
          — Betrag und Zeitpunkt dort anpassen.
        </p>
      ) : planung ? (
        // Enter in einem der beiden Felder übernimmt hier, statt das Formular
        // der Seite abzuschicken, in dem die Karte steht.
        <div
          className="space-y-3"
          onKeyDown={(e) => {
            // Steht im Betragsfeld eine Rechnung, fängt es Enter selbst ab und
            // rechnet aus; erst ein zweites Enter übernimmt.
            if (e.defaultPrevented || e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return;
            e.preventDefault();
            uebernehmen();
          }}
        >
          <p className="t-small text-muted-foreground">
            Als geplante Einnahme (Entwurf) übernehmen — erwarteter Betrag und Zeitpunkt.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Betragsfeld
              label="Erwarteter Zufluss (€)"
              required
              value={planung.betrag ?? ''}
              error={fehler.betrag}
              onChange={(wert) => {
                setPlanung((alt) => (alt ? { ...alt, betrag: wert } : alt));
                setFehler((alt) => ohne(alt, 'betrag'));
              }}
            />
            <TextField
              label="Erwartet am"
              type="date"
              required
              value={planung.datum}
              error={fehler.datum}
              onChange={(e) => {
                const wert = e.target.value;
                setPlanung((alt) => (alt ? { ...alt, datum: wert } : alt));
                setFehler((alt) => ohne(alt, 'datum'));
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={planen.isPending} onClick={uebernehmen}>
              In Planung übernehmen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPlanung(null)}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground">Noch nicht in der Planung.</p>
          <Button size="sm" icon={CalendarPlus} onClick={() => setPlanung(planungVorschlag(p))}>
            In Planung übernehmen
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Vorschlag für die Übernahme: Höchstbetrag und Frist, sonst in drei Monaten. */
function planungVorschlag(p: RadarProgram): { betrag: string; datum: string } {
  const inDreiMonaten = new Date();
  inDreiMonaten.setMonth(inDreiMonaten.getMonth() + 3);
  return {
    betrag: p.amount_max ?? p.amount_min ?? '',
    datum: p.deadline ?? p.event_date ?? inDreiMonaten.toISOString().slice(0, 10),
  };
}
